import sgp4init from "satellite.js/lib/propagation/sgp4init";
import { days2mdhms, jday } from "satellite.js/lib/ext";
import { DateTime } from "luxon";
import { mjd2unix } from ".";
export const pi = Math.PI;
export const twoPi = pi * 2;
export const deg2rad = pi / 180.0;
export const rad2deg = 180 / pi;
export const minutesPerDay = 1440.0;
export const mu = 398600.5;
export const earthRadius = 6378.137;
export const xke = 60.0 / Math.sqrt((earthRadius * earthRadius * earthRadius) / mu);
export const vkmpersec = (earthRadius * xke) / 60.0;
export const tumin = 1.0 / xke;
export function getStaRec(meanElements) {
    const opsmode = "i";
    const xpdotp = 1440.0 / (2.0 * Math.PI);
    let year = 0;
    const satrec = {
        error: 0,
    };
    satrec.satnum = meanElements.id.toString();
    satrec.epochyr = Math.trunc(meanElements.orbitDetTime % 1000);
    satrec.epochdays = meanElements.orbitDetTime - satrec.epochyr * 1000;
    satrec.ndot = meanElements.meanMotionDot;
    satrec.nddot = meanElements.meanMotionDDot;
    satrec.bstar = meanElements.bStar;
    satrec.inclo = meanElements.inclination;
    satrec.nodeo = meanElements.ascendingNode;
    satrec.ecco = meanElements.eccentricity;
    satrec.argpo = meanElements.argPericenter;
    satrec.mo = meanElements.meanAnomaly;
    satrec.a = meanElements.semiMajorAxis;
    const gm = meanElements.gM;
    satrec.no = satrec.a ** (-3.0 / 2.0) * Math.sqrt(gm);
    satrec.ndot /= xpdotp * 1440.0;
    satrec.nddot /= xpdotp * 1440.0 * 1440;
    satrec.inclo *= deg2rad;
    satrec.nodeo *= deg2rad;
    satrec.argpo *= deg2rad;
    satrec.mo *= deg2rad;
    satrec.alta = satrec.a * (1.0 + satrec.ecco) - 1.0;
    satrec.altp = satrec.a * (1.0 - satrec.ecco) - 1.0;
    if (satrec.epochyr < 57) {
        year = satrec.epochyr + 2000;
    }
    else {
        year = satrec.epochyr + 1900;
    }
    const mdhmsResult = days2mdhms(year, satrec.epochdays);
    const { mon, day, hr, minute, sec } = mdhmsResult;
    satrec.jdsatepoch = jday(year, mon, day, hr, minute, sec);
    sgp4init(satrec, {
        opsmode,
        satn: satrec.satnum,
        epoch: satrec.jdsatepoch - 2433281.5,
        xbstar: satrec.bstar,
        xecco: satrec.ecco,
        xargpo: satrec.argpo,
        xinclo: satrec.inclo,
        xmo: satrec.mo,
        xno: satrec.no,
        xnodeo: satrec.nodeo,
    });
    return satrec;
}
export const OMM2TLELines = (omm) => {
    const tleParameters = omm.tleParameters;
    const meanElements = omm.meanKeplerianElements;
    const satnum = Number.parseInt(tleParameters.noradCatId.toString())
        .toString()
        .slice(0, 5)
        .padStart(5, "0");
    const epoch = typeof meanElements.epoch === "number"
        ? DateTime.fromMillis(mjd2unix(meanElements.epoch))
        : DateTime.fromISO(meanElements.epoch);
    const epochyr = epoch.year.toString().slice(2, 4);
    const doy = epoch.diff(DateTime.utc(epoch.year)).as("days");
    const epochDay = doy.toPrecision(11);
    const ndot = tleParameters.meanMotionDot.toPrecision(9).slice(0, 10);
    const nddot = number2TleEponential(tleParameters.meanMotionDdot);
    const bstar = number2TleEponential(tleParameters.bstar);
    const line1 = `1 ${satnum}U NNNNNAAA ${epochyr}${epochDay} ${ndot} ${nddot} ${bstar} N NNNNN`;
    const inclo = meanElements.inclination.toPrecision(7).slice(0, 8);
    const nodeo = meanElements.raOfAscNode.toPrecision(7).slice(0, 8);
    const ecc = getDecimals(meanElements.eccentricity);
    const argpo = meanElements.argOfPericenter.toPrecision(7).slice(0, 8);
    const mo = meanElements.meanAnomaly.toPrecision(7).slice(0, 8);
    const no = getMeanMotion(omm).toPrecision(9).slice(0, 10);
    const line2 = `2 NNNNN ${inclo} ${nodeo} ${ecc} ${argpo} ${mo} ${no}NNNNNNN`;
    return { line1, line2 };
};
const number2TleEponential = (value) => {
    const expString = value.toExponential();
    const [decimal, exponent] = expString.split("e");
    const exp = `${getSign(Number(exponent))}${(Number(exponent) + 1)
        .toString()
        .replace("-", "")}`;
    return `${getSign(value)}${decimal
        .replace("-", "")
        .replace(".", "")
        .slice(0, 5)
        .padEnd(5, "0")}${exp}`;
};
const getDecimals = (value, length = 7) => {
    const digits = value.toFixed(length).split(".")[1];
    return digits;
};
export const getMeanMotion = (omm) => {
    const mo = omm.meanKeplerianElements.meanMotion;
    const µ = omm.meanKeplerianElements.gm;
    const meanMotionFromSMA = (a) => (Math.sqrt(µ / a ** 3) * 86400.0) / 2.0 / Math.PI;
    const sma_mo = meanMotionFromSMA(omm.meanKeplerianElements.semiMajorAxis);
    return mo ? mo : sma_mo;
};
const getSign = (value) => {
    return Math.sign(value) < 0 ? "-" : "+";
};
export const parseTLESourceString = (sourceTLE) => {
    const TLE_LineRegex = /(.*?)\n(.*?)\n(.*)\n/g;
    const matches = sourceTLE.match(TLE_LineRegex);
    if (matches === null) {
        throw new Error("Could not parse TLE Data");
    }
    const TLEData = matches.map((sat) => {
        TLE_LineRegex.lastIndex = 0;
        const match = TLE_LineRegex.exec(sat);
        const name = match[1].trim();
        const line1 = match[2].trim();
        const line2 = match[3].trim();
        return {
            name,
            line1,
            line2,
        };
    });
    return TLEData;
};
//# sourceMappingURL=tle.js.map